import { useState } from 'react';
import classNames from 'classnames';

import FirstServiceCard from './FirstServiceCard';
import SortableServicesModule from './SortableServicesModule';
import {
  useProviderServicesQuery,
  useServiceGroupsQuery,
  ProviderService,
  useArchiveProviderServiceMutation,
} from '../../../generated/graphql';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import ServiceCard from '../ServiceCard';
import { customToast } from '../ToastAlert/customToast';
import { useHomecomingLoader } from '../../hooks/useHomecomingLoader';
import { useAuth } from '../../../contexts/AuthContext';

const ServicesModule: React.FC<{
  className?: string;
}> = ({ className }) => {
  const { authedProviderUser } = useAuth();
  const { data: providerServicesData, loading: loadingProviderServices } =
    useProviderServicesQuery();
  const { data: serviceGroupsData, loading: loadingServiceGroups } =
    useServiceGroupsQuery();
  const providerServices = providerServicesData?.providerServices;
  const HomecomingLoader = useHomecomingLoader();

  const [isDeleteServiceModalOpen, setIsDeleteServiceModalOpen] =
    useState(false);

  const [providerServiceToDelete, setProviderServiceToDelete] =
    useState<ProviderService | null>(null);

  const handleDeleteService = (service: ProviderService) => {
    setProviderServiceToDelete(service);
    setIsDeleteServiceModalOpen(true);
  };

  const [archiveProviderService, { loading: archiveProviderServiceLoading }] =
    useArchiveProviderServiceMutation();

  // Use sortable version if service groups exist (after migration, all providers should have groups)
  const hasGroups =
    serviceGroupsData?.serviceGroups &&
    serviceGroupsData.serviceGroups.length > 0;
  const isLoading = loadingProviderServices || loadingServiceGroups;

  if (isLoading) {
    return (
      <div className={classNames('px-6 py-10', className)}>
        <div className="flex h-[20vh] w-full flex-col justify-center">
          {HomecomingLoader}
        </div>
      </div>
    );
  }

  if (hasGroups) {
    return (
      <>
        <SortableServicesModule
          className={className}
          handleDeleteService={handleDeleteService}
        />
        <ConfirmDeleteModal
          isOpen={isDeleteServiceModalOpen}
          setClosed={() => setIsDeleteServiceModalOpen(false)}
          title={`Delete ${providerServiceToDelete?.name}`}
          fetching={loadingProviderServices || archiveProviderServiceLoading}
          actionButtonTitle="Delete service"
          performDelete={async () => {
            try {
              await archiveProviderService({
                variables: { providerServiceId: providerServiceToDelete.id },
                refetchQueries: ['ServiceGroups', 'ProviderServices'],
              });
              customToast.success('Service deleted successfully');
            } catch (error) {
              customToast.error('Failed to delete service');
            }
          }}
        />
      </>
    );
  }

  // Fallback to original implementation for backwards compatibility
  return (
    <>
      <div className={classNames('px-6 py-10', className)}>
        {!providerServices?.length ? (
          <FirstServiceCard />
        ) : (
          <div className="flex flex-wrap justify-start gap-6">
            {providerServices?.map((providerService: ProviderService) => (
              <ServiceCard
                key={providerService.id}
                service={providerService}
                handleDeleteService={handleDeleteService}
                providerPrimaryColor={authedProviderUser?.primaryColor}
              />
            ))}
          </div>
        )}
      </div>
      <ConfirmDeleteModal
        isOpen={isDeleteServiceModalOpen}
        setClosed={() => setIsDeleteServiceModalOpen(false)}
        title={`Delete ${providerServiceToDelete?.name}`}
        fetching={loadingProviderServices || archiveProviderServiceLoading}
        actionButtonTitle="Delete service"
        performDelete={async () => {
          try {
            await archiveProviderService({
              variables: { providerServiceId: providerServiceToDelete.id },
              refetchQueries: ['ServiceGroups', 'ProviderServices'],
            });
            customToast.success('Service deleted successfully');
          } catch (error) {
            customToast.error('Failed to delete service');
          }
        }}
      />
    </>
  );
};

export default ServicesModule;
